// ==========================================
// FILE: src/components/features/Friends/FriendsPage.js
// ==========================================
import React, { useState, useEffect } from 'react';
import { Users, Mail, UserPlus } from 'lucide-react';
import { useFriends } from '../../../hooks/useFriends';

const FriendsPage = ({ user }) => {
  const { friends, receivedRequests, sentRequests, loadFriends, loadReceivedRequests, loadSentRequests, sendRequest, acceptRequest, rejectRequest, removeFriend, searchUsers } = useFriends();
  const [activeTab, setActiveTab] = useState('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFriends();
    loadReceivedRequests();
    loadSentRequests();
  }, []);

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const results = await searchUsers(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Users size={28} className="text-teal-500" />
          Friends
        </h2>

        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('friends')}
            className={`px-4 py-2 font-semibold ${activeTab === 'friends' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-600'}`}
          >
            Friends ({friends.length})
          </button>
          <button
            onClick={() => setActiveTab('received')}
            className={`px-4 py-2 font-semibold ${activeTab === 'received' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-600'}`}
          >
            Requests ({receivedRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 font-semibold ${activeTab === 'search' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-gray-600'}`}
          >
            Add Friends
          </button>
        </div>

        {activeTab === 'friends' && (
          <div className="space-y-3">
            {friends.length === 0 ? (
              <p className="text-gray-500">No friends yet</p>
            ) : (
              friends.map((friend) => (
                <div key={friend.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                      {friend.username[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold">{friend.username}</p>
                      <p className="text-sm text-gray-500">{friend.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFriend(friend.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'received' && (
          <div className="space-y-3">
            {receivedRequests.length === 0 ? (
              <p className="text-gray-500">No pending requests</p>
            ) : (
              receivedRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      {request.user.username[0].toUpperCase()}
                    </div>
                    <p className="font-semibold">{request.user.username}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => acceptRequest(request.id)}
                      className="px-4 py-2 bg-green-500 text-white rounded text-sm"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => rejectRequest(request.id)}
                      className="px-4 py-2 bg-gray-500 text-white rounded text-sm"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'search' && (
          <div>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search users..."
                className="flex-1 px-4 py-2 border rounded-lg"
              />
              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-teal-500 text-white rounded"
              >
                Search
              </button>
            </div>
            <div className="space-y-3">
              {searchResults.map((foundUser) => (
                <div key={foundUser.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <p className="font-semibold">{foundUser.username}</p>
                  <button
                    onClick={() => sendRequest(foundUser.id)}
                    className="px-4 py-2 bg-teal-500 text-white rounded text-sm flex items-center gap-2"
                  >
                    <UserPlus size={14} />
                    Add
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsPage;